import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AssessmentContext } from "../../contexts/assessments";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import WelcomeReport from "../../components/WelcomeReport";
import BannerReport from "../../components/BannerReport";
import BlockReport from "../../components/BlockReport";
import bgImage from "../../assets/images/bgImage.png";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GroupContext } from "../../contexts/group";
import { getAllAssessment, getCompleteDate, getUserTIMRol, getUserEvalInfo, createAnswer, updateAnswer, getExtraAnswer } from "../../axios/axiosFunctions";
import { UserContext } from "../../contexts/user";
import BlockReportTIM from "../../components/BlockReportTIM";
import BlockReport360 from "../../components/BlockReport360";
import BlockReportTIMSpecial from "../../components/BlockReportTIMSpecial";
import BlockReportIntro from "../../components/BlockReportIntro";
import Logo from "../../assets/images/K2GLogo2.svg";
import { PDFDownloadLink } from "@react-pdf/renderer";
import AssessmentPDF from "../../components/PDF/AssessmentPDF";
import AssessmentTIMPDF from "../../components/PDF/AssessmentTIMPDF";
import Assessment360PDF from "../../components/PDF/Assessment360PDF";
import BlockEndTIM from "../../components/BlockEndTIM";
import BlockReport360Intro from "../../components/BlockReport360Intro";
import BlockEnd360 from "../../components/BlockEnd360";
import BlockEndSelf from "../../components/BlockEndSelf";
import BannerReportTIM from "../../components/BannerReportTIM";

const ReportAssessment = () => {
  const navigate = useNavigate();
  const { slugAssessment, groupId, reportUserId } = useParams();
  const { getAssessmentBySlug, isLoading } = useContext(AssessmentContext);
  const { currentGroup } = useContext(GroupContext);
  const { currentUser } = useContext(UserContext);
  const [currentAssessment, setCurrentAssessment] = useState();
  const [blocks, setBlocks] = useState([]);
  const [chartData, setChartData] = useState({});
  const [summaryIntroImg, setSummaryIntroImg] = useState(null);
  const [sectionsIntroImg, setSectionsIntroImg] = useState(null);
  const [timQuestionsData, setTimQuestionsData] = useState({});
  const [timBlocksOverallData, setTimBlocksOverallData] = useState(null);
  const [blockNotes, setBlockNotes] = useState({});
  const [timFeedbackByBlock, setTimFeedbackByBlock] = useState({});
  const [dateCompleted, setDateCompleted] = useState();
  const [extraQ, setExtraQ] = useState();
  const [revieweeName, setRevieweeName] = useState("");
  const [summaryScores360, setSummaryScores360] = useState([]);
  const [responseRate360, setResponseRate360] = useState(null);

  const [blockReflections, setBlockReflections] = useState({});
  const [blockReflectionIds, setBlockReflectionIds] = useState({});

  // 360 raw data (no images needed -- PDF uses native vector charts)
  const [chartData360, setChartData360] = useState({});
  const [sectionsData360, setSectionsData360] = useState([]);

  // Determine which userId to use for data fetching
  // If reportUserId is in URL (admin preview), use that; otherwise use currentUser
  const effectiveUserId = reportUserId || currentUser._id;
  const isAdminPreview = !!reportUserId;

  useEffect(() => {
    const callCompleteDate = async () => {
      const res = await getCompleteDate(effectiveUserId, slugAssessment);
      if (res) {
        const { dateCompleted: dateC } = res;
        if (dateC) {
          const d = new Date(dateC);
          setDateCompleted(d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
        } else {
          setDateCompleted(dateC);
        }
      }
    };
    callCompleteDate();
  }, [effectiveUserId, slugAssessment]);

  // Fetch reviewee name when admin is previewing a 360 report
  useEffect(() => {
    if (isAdminPreview) {
      const fetchRevieweeName = async () => {
        try {
          const res = await getUserEvalInfo(reportUserId);
          if (res?.user) {
            setRevieweeName(`${res.user.firstName} ${res.user.lastName || ""}`.trim());
          }
        } catch (err) {
          console.log("Error fetching reviewee info:", err);
        }
      };
      fetchRevieweeName();
    }
  }, [reportUserId, isAdminPreview]);

  useEffect(() => {
    if (!isLoading) {
      const assessment = getAssessmentBySlug(slugAssessment);
      if (!assessment) {
        // Assessment not in user context (e.g. admin previewing 360 report)
        // Fetch directly from API
        const fetchAssessment = async () => {
          try {
            const allAssessments = await getAllAssessment();
            const found = allAssessments?.find((a) => a.slug === slugAssessment);
            if (!found) {
              navigate("/");
              return;
            }
            setCurrentAssessment(found);
            setBlocks(found.blocks);
          } catch (err) {
            console.log("Error fetching assessment for report:", err);
            navigate("/");
          }
        };
        fetchAssessment();
        return;
      }
      setCurrentAssessment(assessment);
      setBlocks(assessment.blocks);
    }
  }, [slugAssessment, isLoading, getAssessmentBySlug, navigate]);

  useEffect(() => {
    if (currentAssessment && (currentGroup || groupId)) {
      callUserType();
    }
  }, [currentAssessment, currentUser, currentGroup, groupId]);

  const callUserType = async () => {
    const effectiveGroupId = groupId || currentGroup?._id;
    const res = await getUserTIMRol(effectiveGroupId, currentUser._id);
    if (res) {
      calculateBlock(res.timRol[0]?.rol || "member");
    }
  };

  const calculateBlock = (rol) => {
    /* if(rol === "member" && blocks.length === currentAssessment?.blocks.length){ */
    const newBloks = [...currentAssessment?.blocks];
    setBlocks(newBloks);
    /* } */
  };

  const saveChartData = (blockId, data) => {
    setChartData((prev) => {
      if (prev[blockId]) return prev;
      return { ...prev, [blockId]: data };
    });
  };

  const pdfKey = useMemo(() => {
    const ids = Object.keys(chartData).sort();
    const notesIds = Object.keys(blockNotes).sort();

    const chartsPart =
      ids.length === 0
        ? "empty"
        : ids.map((id) => `${id}:${(chartData[id] || []).length}`).join("|");

    const notesPart =
      notesIds.length === 0
        ? "no-notes"
        : notesIds
            .map((id) => `${id}:${(blockNotes[id] ?? "").length}`)
            .join("|");

    return `${chartsPart}__${notesPart}`;
  }, [chartData, blockNotes]);

  const pdfKey360 = useMemo(() => {
    const blockIds = Object.keys(chartData360).sort();
    if (blockIds.length === 0) return "empty-360";
    return blockIds
      .map((id) => `${id}:${(chartData360[id] || []).length}`)
      .join("|");
  }, [chartData360]);

  const timQuestionsCount = Object.keys(timQuestionsData).length;

  const timQuestionsReady =
    blocks.length > 0 && timQuestionsCount === blocks.length;

  const allTIMGlobalReady = !!timBlocksOverallData;

  const allTIMReady = timQuestionsReady && allTIMGlobalReady;

  // 360 readiness: all blocks have reported their data
  const chart360Count = Object.keys(chartData360).length;
  const all360Ready =
    (currentAssessment?.slug === "mim_360_assessment" ||
      currentAssessment?.slug === "lim_360_assessment") &&
    blocks.length > 0 &&
    chart360Count === blocks.length;

  // Display name for PDFs: use reviewee name for admin preview, otherwise current user
  const displayName = isAdminPreview && revieweeName
    ? revieweeName
    : `${currentUser.firstName} ${currentUser.lastName}`;

  // Group object for 360 report: use currentGroup if available, or create from groupId param
  const reportGroup = currentGroup || (groupId ? { _id: groupId } : null);

  const handleBlockNoteChange = (blockId, text) => {
    setBlockNotes((prev) =>
      prev[blockId] === text ? prev : { ...prev, [blockId]: text },
    );
  };

  const handleExtraQChange = (text) => {
    setExtraQ(text);
  };

  const handleTimFeedbackReady = (blockId, feedbackArray) => {
    setTimFeedbackByBlock((prev) => {
      // Evita actualizar si es igual (opcional pero consistente con lo demas)
      const prevForBlock = prev[blockId] || [];
      if (prevForBlock.length === feedbackArray.length) {
        const same = prevForBlock.every(
          (item, i) => item.answer === feedbackArray[i].answer,
        );
        if (same) return prev;
      }
      return { ...prev, [blockId]: feedbackArray };
    });
  };

  return (
    <Container
      maxWidth="false"
      sx={{
        backgroundColor: "#e8f4f8",
        padding: "0px !important",
      }}
    >
      {isLoading && <CircularProgress />}
      {currentAssessment && !isLoading && (
        <>
          <Box
            display="flex"
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{ marginBottom: "0px", padding: "16px 48px", backgroundColor: "#fff" }}
          >
            {(currentAssessment.slug === "mim_self_assessment" ||
              currentAssessment.slug === "lim_self_assessment") && (
              <Box display="flex" alignItems="center" gap={2}>
                {Object.keys(chartData).length > 0 &&
                  Object.keys(chartData).length === blocks.length && (
                    <PDFDownloadLink
                      key={pdfKey}
                      style={{textDecoration:"none"}}
                      document={
                        <AssessmentPDF
                          chartData={chartData}
                          blocks={blocks}
                          nameA={currentAssessment.name}
                          slug={currentAssessment.slug}
                          report={currentAssessment.report}
                          summaryIntroImg={summaryIntroImg}
                          name={`${currentUser.firstName} ${currentUser.lastName}`}
                          sectionsIntroImg={sectionsIntroImg}
                          info={currentAssessment?.report?.blocksInfo}
                          blockNotes={blockNotes}
                          dateCompleted={dateCompleted}
                          extraQ={extraQ}
                        />
                      }
                      fileName={`${slugAssessment}.pdf`}
                    >
                      {({ loading }) =>
                        loading ? (
                          <Button>
                            Generating PDF
                            <CircularProgress />
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<PrintIcon />}
                          >
                            DOWNLOAD
                          </Button>
                        )
                      }
                    </PDFDownloadLink>
                  )}
                <Typography variant="body2" color="secondary" sx={{fontStyle:"italic",opacity:0.8,maxWidth:"400px"}}>Click download to generate your report with any reflections you entered. (Make sure you have saved on each page first)</Typography>
              </Box>
            )}
            {currentAssessment.slug === "tim_self_assessment" &&
              allTIMReady && (
                <>
                  <PDFDownloadLink
                    key={pdfKey}
                    document={
                      <AssessmentTIMPDF
                        report={currentAssessment.report}
                        name={`${currentUser.firstName} ${currentUser.lastName}`}
                        blocks={blocks}
                        questionsData={timQuestionsData}
                        blocksOverallData={timBlocksOverallData}
                        groupName={currentGroup?.name}
                        companyName={currentGroup?.company?.name}
                        info={currentAssessment?.report?.blocksInfo}
                        feedbackByBlock={timFeedbackByBlock}
                        dateCompleted={dateCompleted}
                        extraQ={extraQ}
                      />
                    }
                    fileName={`${slugAssessment}.pdf`}
                  >
                    {({ loading }) =>
                      loading ? (
                        <Button>
                          Generating PDF... <CircularProgress />
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<PrintIcon />}
                        >
                          DOWNLOAD
                        </Button>
                      )
                    }
                  </PDFDownloadLink>
                </>
              )}
            {(currentAssessment.slug === "mim_360_assessment" ||
              currentAssessment.slug === "lim_360_assessment") &&
              all360Ready && (
                <PDFDownloadLink
                  key={pdfKey360}
                  document={
                    <Assessment360PDF
                      chartData360={chartData360}
                      blocks={blocks}
                      name={displayName}
                      slug={currentAssessment.slug}
                      report={currentAssessment.report || {}}
                      info={currentAssessment?.report?.blocksInfo || []}
                      sectionsData360={sectionsData360}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                      summaryScores360={summaryScores360}
                      responseRate={responseRate360}
                      blockReflections={blockReflections}
                    />
                  }
                  fileName={`${slugAssessment}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>
                        Generating PDF
                        <CircularProgress />
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PrintIcon />}
                      >
                        DOWNLOAD
                      </Button>
                    )
                  }
                </PDFDownloadLink>
              )}
            <Button
              component={Link}
              to={"/"}
              variant="contained"
              startIcon={<ArrowBackIcon className="invertIcon" />}
            >
              BACK
            </Button>
          </Box>
          <Box display="flex" flexDirection="column">
            <BannerReportTIM image={true} text={currentAssessment?.name} />
            {(currentAssessment.slug === "mim_360_assessment" || currentAssessment.slug === "lim_360_assessment") && (
              <Box sx={{ padding: "16px 48px" }}>
                <Typography variant="h5" color="primary" fontWeight={600}>{displayName}</Typography>
                {dateCompleted && <Typography variant="h6" sx={{ color: "#69AF45" }}>{dateCompleted}</Typography>}
              </Box>
            )}
            <WelcomeReport
              slug={currentAssessment?.slug}
              text={currentAssessment?.report?.welcome}
              mainImage={currentAssessment?.report?.mainImage}
              howToUse={currentAssessment?.report?.howToUse}
              sectionTitle={currentAssessment?.slug === "tim_self_assessment" ? "Team Effectiveness Health Check Report" : undefined}
            />
          </Box>
          {currentAssessment.slug === "tim_self_assessment" && (
            <BlockReportTIMSpecial
              onBlocksDataReady={setTimBlocksOverallData}
              groupId={groupId}
            />
          )}
          {(currentAssessment.slug === "mim_self_assessment" ||
            currentAssessment.slug === "lim_self_assessment") && (
            <BlockReportIntro
              slug={currentAssessment.slug}
              onSummaryChartReady={setSummaryIntroImg}
              onSectionsChartReady={setSectionsIntroImg}
            />
          )}
          {(currentAssessment.slug === "mim_360_assessment" ||
            currentAssessment.slug === "lim_360_assessment") && (
            <BlockReport360Intro
              slug={currentAssessment.slug}
              group={reportGroup}
              reportUserId={reportUserId}
              onSectionsDataReady={setSectionsData360}
              onSummaryScoresReady={setSummaryScores360}
              onResponseRateReady={setResponseRate360}
            />
          )}
          {blocks.map((b, i) => (
            <Fragment key={i}>
              <BannerReport number={i + 1} name={b.name} color={b.color} />
              {currentAssessment.slug === "tim_self_assessment" && (
                <BlockReportTIM
                  slug={b.slug}
                  blockId={b._id}
                  color={b.color}
                  info={currentAssessment?.report?.blocksInfo}
                  groupId={groupId}
                  onQuestionsDataReady={(data) =>
                    setTimQuestionsData((prev) =>
                      prev[b._id] ? prev : { ...prev, [b._id]: data },
                    )
                  }
                  onFeedbackReady={(feedbackArray) =>
                    handleTimFeedbackReady(b._id, feedbackArray)
                  }
                />
              )}
              {(currentAssessment.slug === "mim_360_assessment" ||
                currentAssessment.slug === "lim_360_assessment") && (
                <>
                <BlockReport360
                  blockId={b._id}
                  name={b.name}
                  info={currentAssessment?.report?.blocksInfo}
                  slug={slugAssessment}
                  color={b.color}
                  reportUserId={reportUserId}
                  onBlock360DataReady={(questionsData) =>
                    setChartData360((prev) => {
                      return { ...prev, [b._id]: questionsData };
                    })
                  }
                />
                {/* Category Score & Reflection */}
                {summaryScores360.length > 0 && (() => {
                  const score = summaryScores360.find(s => String(s.blockId) === String(b._id));
                  if (!score) return null;
                  const pct = score.positive;
                  const rating = pct >= 90 ? "STRENGTH" : pct >= 60 ? "DEVELOPING" : "OPPORTUNITY";
                  const ratingColor = pct >= 90 ? "#69AF45" : pct >= 60 ? "#2B8DCC" : "#e67e22";
                  const reflectionSlug = `${currentAssessment.slug}_reflection_${b._id}`;
                  const isAdminPreview = !!reportUserId;
                  const loadReflection = async () => {
                    const res = await getExtraAnswer(reflectionSlug, reportUserId || currentUser._id);
                    if (res?.answer) {
                      setBlockReflections(prev => ({ ...prev, [b._id]: res.answer.answer }));
                      setBlockReflectionIds(prev => ({ ...prev, [b._id]: res.answer._id }));
                    }
                  };
                  if (!(b._id in blockReflections)) {
                    loadReflection();
                    setBlockReflections(prev => ({ ...prev, [b._id]: "" }));
                  }
                  const saveReflection = async () => {
                    const text = blockReflections[b._id] || "";
                    let res;
                    if (blockReflectionIds[b._id]) {
                      res = await updateAnswer(blockReflectionIds[b._id], { answer: text });
                    } else {
                      res = await createAnswer({ user: currentUser._id, answer: text, slug: reflectionSlug });
                    }
                    if (res?.answer) setBlockReflectionIds(prev => ({ ...prev, [b._id]: res.answer._id }));
                  };
                  return (
                    <Box sx={{ padding: "0px 48px", margin: "24px 0 40px 0" }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: b.color || "#0F5385", mr: 1.5 }} />
                        <Typography variant="h6" color="secondary" fontWeight={600}>Category Score + Reflection</Typography>
                      </Box>
                      <Box sx={{ backgroundColor: "#F7FAFC", p: 3, borderLeft: `4px solid ${ratingColor}`, mb: 2 }}>
                        <Typography variant="body1" color="secondary" fontWeight={600} sx={{ fontSize: "1.1rem" }}>{b.name}</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{ color: ratingColor, mt: 1 }}>{rating}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Score: {pct.toFixed(0)}%</Typography>
                      </Box>
                      <Typography variant="body1" color="secondary" fontWeight={400} sx={{ fontSize: "1.05rem", lineHeight: "1.7", mb: 2 }}>
                        This category "{b.name}" has been identified {rating} for you. As you reviewed the scores on each question what stands out as a priority focus area in this dimension? Where you might want to focus?
                      </Typography>
                      <TextField
                        value={blockReflections[b._id] || ""}
                        onChange={(e) => setBlockReflections(prev => ({ ...prev, [b._id]: e.target.value }))}
                        fullWidth multiline rows={4}
                        placeholder="Write your reflection here..."
                        disabled={isAdminPreview}
                      />
                      {!isAdminPreview && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, mt: 1 }}>
                          <Typography variant="body2" color="secondary" sx={{fontStyle:"italic",opacity:0.8}}>Make sure to SAVE on each section.</Typography>
                          <Button onClick={saveReflection} variant="contained" size="small" startIcon={<SaveIcon />}>Save</Button>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                </>
              )}
              {(currentAssessment.slug === "mim_self_assessment" ||
                currentAssessment.slug === "lim_self_assessment") && (
                <BlockReport
                  onChartDataReady={saveChartData}
                  info={currentAssessment?.report?.blocksInfo}
                  blockId={b._id}
                  color={b.color}
                  questionLength={b.questions.length}
                  onBlockNoteChange={handleBlockNoteChange}
                />
              )}
            </Fragment>
          ))}
          {
            <Fragment>
              {currentAssessment.slug === "tim_self_assessment" && (
                <BlockEndTIM
                  slug={currentAssessment.slug}
                  onExtraQChange={handleExtraQChange}
                />
              )}
              {(currentAssessment.slug === "mim_360_assessment" ||
                currentAssessment.slug === "lim_360_assessment") && (
                <>
                  <BannerReportTIM text={"Focus Areas for Development"} />
                  <BlockEnd360
                    slug={currentAssessment.slug}
                    onExtraQChange={handleExtraQChange}
                    reportUserId={reportUserId}
                    summaryScores360={summaryScores360}
                  />
                </>
              )}
              {(currentAssessment.slug === "mim_self_assessment" ||
                currentAssessment.slug === "lim_self_assessment") && (
                <>
                  <BannerReportTIM text={"Focus Areas for Development"} />
                  <BlockEndSelf
                    slug={currentAssessment.slug}
                    onExtraQChange={handleExtraQChange}
                  />
                </>
              )}
            </Fragment>
          }
          {/* Bottom download button */}
          <Box display="flex" alignItems="center" gap={2} sx={{ padding: "24px 48px 48px", justifyContent: "flex-start" }}>
            {(currentAssessment.slug === "mim_self_assessment" ||
              currentAssessment.slug === "lim_self_assessment") &&
              Object.keys(chartData).length > 0 &&
              Object.keys(chartData).length === blocks.length && (
              <>
                <PDFDownloadLink
                  key={`bottom-${pdfKey}`}
                  document={
                    <AssessmentPDF
                      chartData={chartData}
                      blocks={blocks}
                      nameA={currentAssessment.name}
                      slug={currentAssessment.slug}
                      report={currentAssessment.report}
                      summaryIntroImg={summaryIntroImg}
                      name={`${currentUser.firstName} ${currentUser.lastName}`}
                      sectionsIntroImg={sectionsIntroImg}
                      info={currentAssessment?.report?.blocksInfo}
                      blockNotes={blockNotes}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                    />
                  }
                  fileName={`${slugAssessment}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>Generating PDF <CircularProgress /></Button>
                    ) : (
                      <Button variant="contained" color="secondary" startIcon={<PrintIcon />}>DOWNLOAD</Button>
                    )
                  }
                </PDFDownloadLink>
                <Typography variant="body2" color="secondary" sx={{fontStyle:"italic",opacity:0.8,maxWidth:"400px"}}>Click download to generate your report with any reflections you entered. (Make sure you have saved on each page first)</Typography>
              </>
            )}
            {currentAssessment.slug === "tim_self_assessment" && allTIMReady && (
              <>
                <PDFDownloadLink
                  key={`bottom-tim-${pdfKey}`}
                  document={
                    <AssessmentTIMPDF
                      report={currentAssessment.report}
                      name={`${currentUser.firstName} ${currentUser.lastName}`}
                      blocks={blocks}
                      questionsData={timQuestionsData}
                      blocksOverallData={timBlocksOverallData}
                      groupName={currentGroup?.name}
                      companyName={currentGroup?.company?.name}
                      info={currentAssessment?.report?.blocksInfo}
                      feedbackByBlock={timFeedbackByBlock}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                    />
                  }
                  fileName={`${slugAssessment}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>Generating PDF... <CircularProgress /></Button>
                    ) : (
                      <Button variant="contained" color="secondary" startIcon={<PrintIcon />}>DOWNLOAD</Button>
                    )
                  }
                </PDFDownloadLink>
                <Typography variant="body2" color="secondary" sx={{fontStyle:"italic",opacity:0.8,maxWidth:"400px"}}>Click download to generate your report with any reflections you entered. (Make sure you have saved on each page first)</Typography>
              </>
            )}
            {(currentAssessment.slug === "mim_360_assessment" ||
              currentAssessment.slug === "lim_360_assessment") && all360Ready && (
              <>
                <PDFDownloadLink
                  key={`bottom-360-${pdfKey360}`}
                  document={
                    <Assessment360PDF
                      chartData360={chartData360}
                      blocks={blocks}
                      name={displayName}
                      slug={currentAssessment.slug}
                      report={currentAssessment.report || {}}
                      info={currentAssessment?.report?.blocksInfo || []}
                      sectionsData360={sectionsData360}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                      summaryScores360={summaryScores360}
                      responseRate={responseRate360}
                      blockReflections={blockReflections}
                    />
                  }
                  fileName={`${slugAssessment}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>Generating PDF... <CircularProgress /></Button>
                    ) : (
                      <Button variant="contained" color="secondary" startIcon={<PrintIcon />}>DOWNLOAD</Button>
                    )
                  }
                </PDFDownloadLink>
                <Typography variant="body2" color="secondary" sx={{fontStyle:"italic",opacity:0.8,maxWidth:"400px"}}>Click download to generate your report with any reflections you entered. (Make sure you have saved on each page first)</Typography>
              </>
            )}
          </Box>
        </>
      )}
    </Container>
  );
};

export default ReportAssessment;